import { Card, Divider, Stack, Typography, styled, useTheme } from "@mui/material"
import {
  AchievementBadge,
  EvolutionUnitStatChip,
  FeatureFlagName,
  formatNumber,
  noDecimalsFormatOptions,
  useFeatureFlag,
} from "@pharmupp/p3-front-commons"
import { type HTMLAttributes, forwardRef } from "react"
import { HealthMissionCardTooltip } from "./HealthMissionCardTooltip"
import { HealthMissionProgressCard } from "./HealthMissionProgressCard"
import type { HealthMission } from "./healthMission.model"

export const HealthMissionCard = (props: HealthMission) => {
  const { get: isGoalsFlagEnabled } = useFeatureFlag(
    FeatureFlagName.HEALTH_MISSIONS_GOAL,
  )
  const displayHealthMissionsGoal =
    isGoalsFlagEnabled && !!props.goalProgress.goals.length

  const content = (
    <HealthMissionCardContent
      {...props}
      displayHealthMissionsGoal={displayHealthMissionsGoal}
    />
  )

  if (!displayHealthMissionsGoal) return content

  return (
    <HealthMissionCardTooltip title={<HealthMissionProgressCard {...props} />}>
      {content}
    </HealthMissionCardTooltip>
  )
}

// Tooltip injects its handlers (onMouseOver, onFocus...) as props: forward them to the DOM
type HealthMissionCardContentProps = HealthMission &
  Omit<HTMLAttributes<HTMLDivElement>, keyof HealthMission> & {
    displayHealthMissionsGoal: boolean
  }

const HealthMissionCardContent = forwardRef<
  HTMLDivElement,
  HealthMissionCardContentProps
>(
  (
    {
      label,
      missionCount,
      evolution,
      goalProgress: { isAchieved },
      displayHealthMissionsGoal,
      ...tooltipProps
    },
    ref,
  ) => {
    const { palette } = useTheme()

    return (
      <MissionCardContainer ref={ref} {...tooltipProps}>
        <Typography variant="14px" color={palette.common.darkBlue}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1.25}>
          <Typography
            variant="18px"
            fontWeight={600}
            color={palette.common.darkBlue}
          >
            {formatNumber(missionCount, noDecimalsFormatOptions)}
          </Typography>
          <EvolutionUnitStatChip
            evol={evolution ?? 0}
            formatOptions={{ signDisplay: "always" }}
          />
          {displayHealthMissionsGoal && (
            <>
              <Divider flexItem orientation="vertical" />
              <AchievementBadge isAchieved={isAchieved} />
            </>
          )}
        </Stack>
      </MissionCardContainer>
    )
  },
)

const MissionCardContainer = styled(Card)(({ theme: { spacing } }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing(2),
  minHeight: "66px",
  paddingInline: spacing(2.5),
  borderRadius: "2000px",
}))
